/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  getAdminDashboardStats,
  getConversations,
} from 'data/api/intercom.api';
import {
  createAsyncThunk,
  createSlice,
  original,
  PayloadAction,
} from '@reduxjs/toolkit';
export interface Tag {
  id: string;
  name: string;
}
export enum ConversationPartTypeEnum {
  admin = 'admin',
  user = 'user',
  bot = 'bot',
}
export interface ConversationPartModel {
  conversationPartId: string;
  conversationType?: string;
  conversationPartType?: string;
  conversationAuthorType?: string;
  conversationPartIntercomId?: string;
  deliveredAs?: string;
  subject?: string;
  body: string;
  conversationPartAuthor?: string;
  url?: string;
  conversationId?: string;
  createdAt?: number;
  updatedAt?: string;
  notifiedAt?: string;
  conversationAssignedTo?: string;
  conversationPartExternalId?: string;
  redacted?: boolean;
  authorName?: string;
  avatar?: string;
  tags?: string[];
  // The same tags with the mention rows behind them; admin removes by these.
  mentionTags?: { name: string; mentionTagIds: string[] }[] | null;
}

export interface IConverstation {
  conversationId: string;
  conversationType?: string;
  conversationIntercomId?: string;
  conversationPart?: ConversationPartModel[];
  tags?: Tag[];
  tagAiPart?: TagAiPartModel[];
}

export interface TagAiPartModel {
  tagName?: string;
  total?: number;
}

export interface EnrollMember {
  id: string;
  companyId: string;
  enrolledMember?: number;
  createdDate?: Date;
}
export interface UniqueMemberEngagement {
  id: string;
  companyId: string;
  conversationPartTypeUserCount?: number;
  conversationPartTypeAdminCount?: number;
  uniqueMemberEngagement?: number;
  createdDate?: Date;
}
export interface MessageResponseRate {
  id: string;
  companyId: string;
  countConversationsRecordUser?: number;
  countConversationsRecord?: number;
  messageResponseRate?: number;
  createdDate?: Date;
}

export interface IDashboard {
  enrollMemberData: {
    enrollMemberValue?: number;
    enrollMembers?: EnrollMember[];
  };
  uniqueMemberData: {
    uniqueMemberValue?: number;
    countConversationsRecordUser?: number;
    countConversationsRecord?: number;
    uniqueMemberEngagements?: UniqueMemberEngagement[];
  };
  messageResponseData: {
    messageResponseRateValue?: number;
    conversationPartTypeUserCount?: number;
    conversationPartTypeAdminCount?: number;
    messageResponseRates?: MessageResponseRate[];
  };
  triages: number;
  possibleFeedback: number;
  optOuts: number;
}

export interface IAdminDashboardStats {
  companyId: string;
  companies?: string[];
  eventType?: string;
  hasBranchCompanies?: boolean;
}

export interface IIntercomState {
  getConversations: 'idle' | 'loading' | 'success' | 'failed';
  converstation: IConverstation | null;
  // The member whose load is current. The cross-member inbox makes it easy to
  // open B while A is still loading; without this, A's late response lands in
  // B's page and shows the wrong member's transcript.
  requestedUserId: string | null;
  // The thunk requestId of that load. The user id alone cannot separate two
  // loads of the SAME member (A -> B -> A): the first one's late response
  // still matches, and would flash stale or failed state over the second.
  requestedLoadId: string | null;
  // Who the transcript in `converstation` actually belongs to. requestedUserId
  // alone cannot answer that: selecting B re-renders with B's header before
  // the fetch effect runs, so for one commit the store still holds A. Readers
  // compare this against the member they are rendering and show nothing until
  // it matches.
  loadedUserId: string | null;
  // The latest background re-read. Polls can finish out of order, and an
  // older answer would take a newer reply back off the screen.
  refreshRequestId: string | null;
  loading: boolean;
  getAdminDashboardStats: 'idle' | 'loading' | 'success' | 'failed';
  dashboardStats: IDashboard | null;
  loadingStats: boolean;
  tag: string;
}

export const getConverstationAsync = createAsyncThunk(
  'intercom/getRelationshipsAsync',
  async (userId: string): Promise<any> => {
    const response = await getConversations(userId);
    return response.conversation;
  },
);

// Re-reads the open transcript without the load cycle: no spinner, and the
// store keeps its object when nothing changed, so the thread doesn't re-render
// or scroll on a quiet poll.
export const refreshConverstationAsync = createAsyncThunk(
  'intercom/refreshConverstationAsync',
  async (userId: string, { getState }) => {
    // The load this refresh was taken against: one started since then is
    // newer than what this response saw.
    const loadId = (getState() as { intercom: IIntercomState }).intercom
      .requestedLoadId;
    const response = await getConversations(userId);
    return { loadId, conversation: response.conversation as IConverstation };
  },
);

export const getAdminDashboardStatsAsync = createAsyncThunk(
  'intercom/getAdminDashboardStatsAsync',
  async (data: IAdminDashboardStats): Promise<IDashboard> => {
    const response = await getAdminDashboardStats(data);
    return response;
  },
);

const initialState: IIntercomState = {
  getConversations: 'idle',
  converstation: null,
  requestedUserId: null,
  requestedLoadId: null,
  loadedUserId: null,
  refreshRequestId: null,
  loading: false,
  dashboardStats: null,
  getAdminDashboardStats: 'idle',
  loadingStats: false,
  tag: '',
};

export const intercomSlice = createSlice({
  name: 'intercom',
  initialState,
  reducers: {
    handlecConverstaion: (state, action) => {
      state.converstation = action.payload;
    },
    handleTag: (state, action) => {
      state.tag = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getConverstationAsync.pending, (state, action) => {
        // Claims the slot; any response for a previously requested member is
        // stale from here on.
        state.requestedUserId = action.meta.arg;
        state.requestedLoadId = action.meta.requestId;
        state.converstation = null;
        state.loadedUserId = null;
        state.loading = true;
        state.getConversations = 'loading';
      })
      .addCase(getConverstationAsync.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.requestedLoadId) return;
        state.converstation = action.payload;
        state.loadedUserId = action.meta.arg;
        state.loading = false;
        state.getConversations = 'success';
      })
      .addCase(getConverstationAsync.rejected, (state, action) => {
        if (action.meta.requestId !== state.requestedLoadId) return;
        state.loading = false;
        state.converstation = null;
        state.loadedUserId = null;
        state.getConversations = 'failed';
      })
      .addCase(refreshConverstationAsync.pending, (state, action) => {
        state.refreshRequestId = action.meta.requestId;
      })
      .addCase(refreshConverstationAsync.fulfilled, (state, action) => {
        // Only onto a settled transcript of the same member, and only if no
        // full load has started since: that one owns the slot.
        if (
          state.getConversations !== 'success' ||
          state.refreshRequestId !== action.meta.requestId ||
          state.loadedUserId !== action.meta.arg ||
          state.requestedLoadId !== action.payload.loadId
        ) {
          return;
        }
        const { conversation } = action.payload;
        const before = state.converstation && original(state.converstation);
        if (JSON.stringify(before) === JSON.stringify(conversation)) return;
        state.converstation = conversation;
      })
      .addCase(getAdminDashboardStatsAsync.pending, (state) => {
        state.loadingStats = true;
      })
      .addCase(
        getAdminDashboardStatsAsync.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.dashboardStats = action.payload;
          state.loadingStats = false;
        },
      )
      .addCase(getAdminDashboardStatsAsync.rejected, (state) => {
        state.loadingStats = false;
        state.dashboardStats = null;
      });
  },
});
export const { handlecConverstaion, handleTag } = intercomSlice.actions;
export default intercomSlice.reducer;
